// =============================================================================
// TeamManagement.jsx - FIXED VERSION (UUID-Based Team Tracking)
// =============================================================================
// Uses stable team IDs instead of array indices to prevent edit collisions
// =============================================================================

import React, { useState, useMemo } from 'react';
import PremiumFeatureModal from '../PremiumFeatureModal';
import TrialExpiredModal from '../TrialExpiredModal';

// Import subscription limits
import {
  getLimitsForTier,
  getUpgradeTiers,
  getTeamLimitBenefits
} from '../../config/subscriptionTiers';

// =============================================================================
// UUID GENERATOR
// =============================================================================
const generateUUID = () => {
  return 'team_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
};

// =============================================================================
// NATURAL SORT HELPER
// =============================================================================
const naturalSortCompare = (a, b) => {
  const nameA = (a.name || '').toLowerCase();
  const nameB = (b.name || '').toLowerCase();
  
  const chunksA = nameA.match(/(\d+|\D+)/g) || [];
  const chunksB = nameB.match(/(\d+|\D+)/g) || [];
  
  const maxLength = Math.max(chunksA.length, chunksB.length);
  
  for (let i = 0; i < maxLength; i++) {
    const chunkA = chunksA[i] || '';
    const chunkB = chunksB[i] || '';
    
    const numA = parseInt(chunkA, 10);
    const numB = parseInt(chunkB, 10);
    
    if (!isNaN(numA) && !isNaN(numB)) {
      if (numA !== numB) {
        return numA - numB;
      }
    } else {
      if (chunkA !== chunkB) {
        return chunkA.localeCompare(chunkB);
      }
    }
  }
  
  return 0;
};

const sortTeamsNaturally = (teamsArray) => {
  return [...teamsArray].sort(naturalSortCompare);
};

const TeamManagement = ({
  teams,
  onTeamsChange,
  onSave,
  showSuccess,
  isSaving = false,
  clubInfo,
  subscriptionTier = 'free'
}) => {
  // ===========================================================================
  // STATE - Now tracking by team ID instead of index
  // ===========================================================================
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState(null); // Changed from editingTeamIndex
  const [showRemoveWarning, setShowRemoveWarning] = useState(false);
  const [teamToRemoveId, setTeamToRemoveId] = useState(null); // Changed from teamToRemoveIndex
  const [errors, setErrors] = useState({});
  const [localTeamName, setLocalTeamName] = useState('');
  
  // Premium modal state
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumModalConfig, setPremiumModalConfig] = useState({});
  
  // Trial expired modal state
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);

  // ===========================================================================
  // SORTED TEAMS - Memoized for performance
  // ===========================================================================
  const sortedTeams = useMemo(() => {
    return sortTeamsNaturally(teams);
  }, [teams]);

  // ===========================================================================
  // HELPER: Find team by ID
  // ===========================================================================
  const findTeamById = (teamId) => {
    return teams.find(t => t.id === teamId);
  };

  const findTeamIndexById = (teamId) => {
    return teams.findIndex(t => t.id === teamId);
  };

  // ===========================================================================
  // TRIAL CHECK
  // ===========================================================================
  const isTrialExpired = () => {
    if (!clubInfo) return false;
    
    const now = new Date();
    const trialEndsAt = clubInfo.trialEndsAt?.toDate ? 
      clubInfo.trialEndsAt.toDate() : 
      new Date(clubInfo.trialEndsAt);
    
    const subscriptionStatus = clubInfo.subscriptionStatus || 'trial';
    const msRemaining = trialEndsAt - now;
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
    
    return subscriptionStatus === 'trial' && daysRemaining <= 0;
  };

  // ===========================================================================
  // SUBSCRIPTION LIMITS
  // ===========================================================================
  const limits = getLimitsForTier(subscriptionTier);
  const currentTeamCount = teams.length;
  const canAddMoreTeams = currentTeamCount < limits.maxTeams;
  const teamsRemaining = limits.maxTeams - currentTeamCount;

  // ===========================================================================
  // VALIDATION
  // ===========================================================================
  const validateTeamName = (name, excludeTeamId = null) => {
    const trimmedName = name.trim();
    
    if (!trimmedName) {
      return 'Team name is required';
    }
    
    if (trimmedName.length > 15) {
      return 'Team name must be 15 characters or less';
    }
    
    // Check for duplicate names (case-insensitive), excluding current team by ID
    const isDuplicate = teams.some((team) => 
      team.id !== excludeTeamId && 
      team.name.toLowerCase() === trimmedName.toLowerCase()
    );
    
    if (isDuplicate) {
      return 'A team with this name already exists';
    }
    
    return null;
  };

  // ===========================================================================
  // HANDLERS: Add Team with Limit Check
  // ===========================================================================
  const handleAddTeam = () => {
    if (!canAddMoreTeams) {
      const upgradeTiers = getUpgradeTiers(subscriptionTier, 'teams');
      setPremiumModalConfig({
        featureName: 'Additional Teams',
        currentTier: subscriptionTier,
        requiredTiers: upgradeTiers.map(t => t.toLowerCase()),
        benefits: getTeamLimitBenefits(upgradeTiers)
      });
      setShowPremiumModal(true);
      return;
    }

    // Generate a new UUID for this team
    const newTeamId = generateUUID();
    
    const newTeam = {
      id: newTeamId, // Stable UUID
      name: '',
      color: '#3b82f6',
      matchArea: 'FULL',
      matchDuration: 60
    };
    
    const updatedTeams = [...teams, newTeam];
    onTeamsChange(updatedTeams);
    
    // Set editing by ID, not index
    setEditingTeamId(newTeamId);
    setLocalTeamName('');
  };

  // ===========================================================================
  // HANDLERS: Team CRUD Operations (now using IDs)
  // ===========================================================================
  const handleTeamNameChange = (teamId, value) => {
    setLocalTeamName(value);
  };

  const handleTeamChange = (teamId, field, value) => {
    const updatedTeams = teams.map(team => 
      team.id === teamId 
        ? { ...team, [field]: value }
        : team
    );
    onTeamsChange(updatedTeams);
  };

  const handleSaveTeam = async (teamId) => {
    // Find the team by ID
    const teamIndex = findTeamIndexById(teamId);
    if (teamIndex === -1) {
      console.error('Team not found:', teamId);
      return;
    }

    // Update the team name from local state
    const updatedTeams = teams.map(team =>
      team.id === teamId
        ? { ...team, name: localTeamName }
        : team
    );
    
    const team = updatedTeams.find(t => t.id === teamId);
    
    // Validation - pass the team ID for exclusion
    const error = validateTeamName(team.name, teamId);
    if (error) {
      setErrors(prev => ({ ...prev, [teamId]: error }));
      return;
    }

    // Clear errors
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[teamId];
      return newErrors;
    });

    // Sort teams before saving (for display consistency)
    const sortedUpdatedTeams = sortTeamsNaturally(updatedTeams);
    
    // Update teams with sorted order
    onTeamsChange(sortedUpdatedTeams);
    setEditingTeamId(null);

    // Save to Firestore
    const success = await onSave(sortedUpdatedTeams);
    if (success) {
      showSuccess('Team saved successfully');
    }
  };

  const handleBlurSave = async (teamId) => {
    // Find the team by ID
    const team = findTeamById(teamId);
    if (!team) return;

    // Only save if the team has a name
    if (!localTeamName || localTeamName.trim() === '') {
      return;
    }

    // Validation
    const error = validateTeamName(localTeamName, teamId);
    if (error) {
      setErrors(prev => ({ ...prev, [teamId]: error }));
      return;
    }

    // Clear errors
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[teamId];
      return newErrors;
    });

    // Update the team with new name
    const updatedTeams = teams.map(t =>
      t.id === teamId
        ? { ...t, name: localTeamName }
        : t
    );

    // Sort teams before saving
    const sortedUpdatedTeams = sortTeamsNaturally(updatedTeams);
    
    // Update teams with sorted order
    onTeamsChange(sortedUpdatedTeams);

    // Save to Firestore
    const success = await onSave(sortedUpdatedTeams);
    if (success) {
      showSuccess('Team saved successfully');
    }
  };

  const handleCancelEdit = (teamId) => {
    const team = findTeamById(teamId);
    
    // If team has no name, it's a new unsaved team - remove it
    if (!team?.name || team.name.trim() === '') {
      const updatedTeams = teams.filter(t => t.id !== teamId);
      onTeamsChange(updatedTeams);
    }
    
    setEditingTeamId(null);
    setLocalTeamName('');
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[teamId];
      return newErrors;
    });
  };

  const handleRemoveTeam = (teamId) => {
    setTeamToRemoveId(teamId);
    setShowRemoveWarning(true);
  };

  const confirmRemoveTeam = async () => {
    if (teamToRemoveId === null) return;

    const updatedTeams = teams.filter(team => team.id !== teamToRemoveId);
    
    // Sort after removing
    const sortedUpdatedTeams = sortTeamsNaturally(updatedTeams);
    onTeamsChange(sortedUpdatedTeams);

    const success = await onSave(sortedUpdatedTeams);
    if (success) {
      showSuccess('Team removed successfully');
    }

    setShowRemoveWarning(false);
    setTeamToRemoveId(null);
    
    // Clear editing state if we were editing this team
    if (editingTeamId === teamToRemoveId) {
      setEditingTeamId(null);
    }
  };

  // ===========================================================================
  // RENDER: Match Area Options
  // ===========================================================================
  const matchAreaOptions = [
    { value: 'FULL', label: 'Full Pitch (8 sections)', sections: 'A,B,C,D,E,F,G,H' },
    { value: 'HALF', label: 'Half Pitch (4 sections)', sections: 'A,B,C,D' },
    { value: 'QUARTER', label: 'Quarter Pitch (2 sections)', sections: 'A,B' },
    { value: 'SMALL', label: 'Single Section', sections: 'A' }
  ];

  // ===========================================================================
  // RENDER: Component
  // ===========================================================================
  return (
    <>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        {/* Header */}
        <div
          onClick={() => {
            if (isTrialExpired()) {
              setShowTrialExpiredModal(true);
              return;
            }
            setIsExpanded(!isExpanded);
          }}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '8px',
            margin: '-8px',
            marginBottom: '8px',
            borderRadius: '8px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
              Team Management
            </h2>
          </div>

          <div style={{
            fontSize: '20px',
            color: '#6b7280',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </div>
        </div>

        <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
          Configure team names & colours. As well as Pitch size & duration for match days.
        </p>

        {/* Content */}
        {isExpanded && (
          <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Team Count with Limit */}
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: 0
                }}>
                  Teams: <strong style={{ color: '#1f2937' }}>{currentTeamCount} / {limits.maxTeams}</strong>
                </p>
                {!canAddMoreTeams && (
                  <p style={{
                    fontSize: '12px',
                    color: '#dc2626',
                    margin: '4px 0 0 0'
                  }}>
                    Team limit reached for {subscriptionTier} tier
                  </p>
                )}
              </div>
              <button
                onClick={handleAddTeam}
                style={{
                  padding: '8px 16px',
                  backgroundColor: canAddMoreTeams ? '#10b981' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: canAddMoreTeams ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <span style={{ fontSize: '16px' }}>+</span>
                Add Team
              </button>
            </div>

            {/* Teams List */}
            {teams.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>⚽</div>
                <p style={{
                  fontSize: '16px',
                  color: '#6b7280',
                  margin: 0
                }}>
                  No teams yet. Click "Add Team" to get started.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {sortedTeams.map((team) => {
                  // Use team.id as the stable identifier
                  const isEditing = editingTeamId === team.id;
                  const teamError = errors[team.id];

                  return (
                    <div
                      key={team.id} // Use stable UUID as key
                      style={{
                        padding: '16px',
                        backgroundColor: isEditing ? '#f0f9ff' : '#f9fafb',
                        border: isEditing ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    >
                      {isEditing ? (
                        // Edit Mode
                        <div>
                          {/* Team Name */}
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{
                              display: 'block',
                              fontSize: '13px',
                              fontWeight: '500',
                              color: '#374151',
                              marginBottom: '6px'
                            }}>
                              Team Name *
                            </label>
                            <input
                              type="text"
                              value={localTeamName}
                              onChange={(e) => handleTeamNameChange(team.id, e.target.value)}
                              onBlur={() => handleBlurSave(team.id)}
                              placeholder="Enter team name"
                              maxLength={15}
                              autoFocus={!team.name}
                              style={{
                                width: '100%',
                                padding: '10px',
                                border: teamError ? '1px solid #dc2626' : '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginTop: '4px'
                            }}>
                              {teamError ? (
                                <span style={{ fontSize: '12px', color: '#dc2626' }}>{teamError}</span>
                              ) : (
                                <span style={{ fontSize: '12px', color: '#9ca3af' }}>Max 15 characters</span>
                              )}
                              <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                                {localTeamName.length}/15
                              </span>
                            </div>
                          </div>

                          {/* Team Color */}
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{
                              display: 'block',
                              fontSize: '13px',
                              fontWeight: '500',
                              color: '#374151',
                              marginBottom: '6px'
                            }}>
                              Team Colour
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <input
                                type="color"
                                value={team.color}
                                onChange={(e) => handleTeamChange(team.id, 'color', e.target.value)}
                                style={{
                                  width: '60px',
                                  height: '40px',
                                  padding: '2px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  cursor: 'pointer'
                                }}
                              />
                              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                                {team.color.toUpperCase()}
                              </span>
                            </div>
                          </div>

                          {/* Match Area */}
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{
                              display: 'block',
                              fontSize: '13px',
                              fontWeight: '500',
                              color: '#374151',
                              marginBottom: '6px'
                            }}>
                              Match Day Pitch Size
                            </label>
                            <select
                              value={team.matchArea}
                              onChange={(e) => handleTeamChange(team.id, 'matchArea', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                backgroundColor: 'white'
                              }}
                            >
                              {matchAreaOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Match Duration */}
                          <div style={{ marginBottom: '16px' }}>
                            <label style={{
                              display: 'block',
                              fontSize: '13px',
                              fontWeight: '500',
                              color: '#374151',
                              marginBottom: '6px'
                            }}>
                              Match Duration (minutes)
                            </label>
                            <input
                              type="number"
                              value={team.matchDuration}
                              onChange={(e) => handleTeamChange(team.id, 'matchDuration', parseInt(e.target.value) || 60)}
                              min={15}
                              max={120}
                              step={5}
                              style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </div>

                          {/* Action Buttons */}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleSaveTeam(team.id)}
                              disabled={isSaving}
                              style={{
                                flex: 1,
                                padding: '8px 16px',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: isSaving ? 'not-allowed' : 'pointer',
                                opacity: isSaving ? 0.6 : 1
                              }}
                            >
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={() => handleCancelEdit(team.id)}
                              disabled={isSaving}
                              style={{
                                flex: 1,
                                padding: '8px 16px',
                                backgroundColor: 'white',
                                color: '#6b7280',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: isSaving ? 'not-allowed' : 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // Display Mode
                        <div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '8px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{
                                width: '24px',
                                height: '24px',
                                backgroundColor: team.color,
                                borderRadius: '4px',
                                border: '1px solid #e5e7eb'
                              }} />
                              <h3 style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#1f2937',
                                margin: 0
                              }}>
                                {team.name || 'Unnamed Team'}
                              </h3>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => {
                                  setEditingTeamId(team.id);
                                  setLocalTeamName(team.name || '');
                                }}
                                disabled={isSaving}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#e0f2fe',
                                  color: '#0369a1',
                                  border: '1px solid #7dd3fc',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  cursor: isSaving ? 'not-allowed' : 'pointer'
                                }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleRemoveTeam(team.id)}
                                disabled={isSaving}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#fee2e2',
                                  color: '#dc2626',
                                  border: '1px solid #fca5a5',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  cursor: isSaving ? 'not-allowed' : 'pointer'
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '8px',
                            fontSize: '13px',
                            color: '#6b7280'
                          }}>
                            <div>
                              <strong>Match Area:</strong> {
                                matchAreaOptions.find(opt => opt.value === team.matchArea)?.label || team.matchArea
                              }
                            </div>
                            <div>
                              <strong>Duration:</strong> {team.matchDuration} minutes
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Premium Feature Modal */}
      <PremiumFeatureModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        featureName={premiumModalConfig.featureName}
        currentTier={premiumModalConfig.currentTier}
        requiredTiers={premiumModalConfig.requiredTiers || []}
        benefits={premiumModalConfig.benefits || []}
      />

      {/* Trial Expired Modal */}
      <TrialExpiredModal
        isOpen={showTrialExpiredModal}
        onClose={() => setShowTrialExpiredModal(false)}
      />

      {/* Remove Team Warning Modal */}
      {showRemoveWarning && teamToRemoveId !== null && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 20px 25px rgba(0, 0, 0, 0.25)'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#dc2626',
              marginTop: 0,
              marginBottom: '12px'
            }}>
              Remove Team?
            </h3>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '20px'
            }}>
              Are you sure you want to remove "{findTeamById(teamToRemoveId)?.name}"? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowRemoveWarning(false);
                  setTeamToRemoveId(null);
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveTeam}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeamManagement;
